import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import * as XLSX from 'xlsx';
import { Loader2, Download, AlertTriangle, RefreshCw, ShieldAlert } from 'lucide-react';

/**
 * CpscComplianceWorklist — CHA-179. The CPSC doc-request worklist (Production Verification → CPSC Docs),
 * replacing the old CPSC Test tab. Lists ALL in-scope US-CPLAN open-WIP production orders (no financials)
 * with each one's CPSC status + what to request from production: a missing **certificate**, or a missing
 * **HTS** code we can't resolve yet. Exportable to Excel to send to production. Read-only, admin-gated.
 *
 * Data: getCpscComplianceRows (full in-scope set) enriched with getCpscStatusForStyles (per-style status).
 */
const s = (v) => String(v ?? '').trim();

// Per-style engine status → worklist status label + the action to request from production.
// Exported for unit tests (§7).
export function classify(info) {
  if (!info) return { status: 'Unknown (no HTS)', action: 'HTS code' };
  if (info.status === 'certified') return { status: 'Certified', action: '' };
  // CHA-222: a valid flammability exemption (16 CFR 1610.1(d)) — no cert required, no document to request.
  if (info.status === 'exempt') return { status: 'Exempt', action: '' };
  // CHA-540: Target TTS says no CPSC certification rule applies. Keep this distinct from an exemption.
  if (info.status === 'not_subject') return { status: 'Not subject', action: '' };
  if (info.cpscApplicable == null) return { status: 'Unknown (no HTS)', action: 'HTS code' }; // no HTS on file
  if (info.status === 'na') return { status: 'Not required', action: '' };
  if (info.status === 'review') return { status: 'Verify (may need CPC)', action: `Verify ${info.certType || 'CPC'}` }; // children's — not sure, flag
  if (info.status === 'none') return { status: 'Missing certificate', action: `CPSC certificate${info.certType ? ` (${info.certType})` : ''}` };
  // submission_error / certification_failed — a draft exists but the CPSC filing FAILED; actionable, must NOT
  // disappear under the "needs only" filter (Codex P2 #83).
  if (info.status === 'failed') return { status: 'Certificate failed', action: `Fix submission${info.certType ? ` (${info.certType})` : ''}` };
  // awaiting / submitted / in_progress — a cert is in flight
  return { status: 'In progress', action: '' };
}

// Columns (operational only — NO cost/price). Order = table + export order.
const COLUMNS = [
  { label: 'PO', field: 'po' }, { label: 'Style', field: 'style' }, { label: 'Description', field: 'styleDesc' },
  { label: 'Customer', field: 'customer' }, { label: 'Division', field: 'division' }, { label: 'Factory', field: 'factory' },
  { label: 'COO', field: 'coo' }, { label: 'Warehouse', field: 'warehouse' }, { label: 'Prod Type', field: 'prodType' },
  { label: 'Stage', field: 'stage' }, { label: 'Ship Date', field: 'shipDate' },
  { label: 'Balance', field: 'balanceUnits', type: 'number', align: 'right' }, // open units left on the PO line
  { label: 'CPSC Status', field: 'cpscStatus' }, { label: 'Action Needed', field: 'actionNeeded' },
];
const STATUS_TONE = {
  'Certified': 'text-green-700 bg-green-50', 'Missing certificate': 'text-amber-700 bg-amber-50',
  'Verify (may need CPC)': 'text-yellow-800 bg-yellow-50',
  'Unknown (no HTS)': 'text-slate-600 bg-slate-100', 'In progress': 'text-blue-700 bg-blue-50',
  'Certificate failed': 'text-red-700 bg-red-50',
  'Not required': 'text-slate-400 bg-white',
  'Exempt': 'text-teal-700 bg-teal-50', // CHA-222 — flammability-exempt (16 CFR 1610.1(d))
  'Not subject': 'text-slate-600 bg-slate-100', // CHA-540 — Target TTS determination
};

export default function CpscComplianceWorklist({ userProfile }) {
  const role = userProfile?.role;
  const isAdmin = role === 'platform_owner' || role === 'admin';
  const tenantId = userProfile?.tenantId || 'inmocean';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [truncated, setTruncated] = useState(false);        // backend hit MAX_ROWS — worklist is incomplete
  const [onlyNeeds, setOnlyNeeds] = useState(true);          // default: only rows needing a document
  const [filter, setFilter] = useState('');
  const [sort, setSort] = useState({ key: 'shipDate', dir: 'asc' });

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true); setError(null); setTruncated(false);
    try {
      const fns = getFunctions();
      const res = await httpsCallable(fns, 'getCpscComplianceRows', { timeout: 150000 })({ tenantId }); // CHA-382 / CHA-528 — server's 120s window + 30s transport headroom
      const base = (res && res.data && res.data.rows) || [];
      setTruncated(!!(res && res.data && res.data.truncated)); // MAX_ROWS hit → the set below is incomplete (Codex P2 #83)
      const styles = [...new Set(base.map((r) => s(r.style)).filter(Boolean))];
      const statusCall = httpsCallable(fns, 'getCpscStatusForStyles', { timeout: 150000 }); // CHA-382
      const statusByStyle = {};
      for (let i = 0; i < styles.length; i += 500) {
        const chunk = styles.slice(i, i + 500);
        const sr = await statusCall({ styleNos: chunk, tenantId });
        Object.assign(statusByStyle, (sr && sr.data && sr.data.statuses) || {});
      }
      setRows(base.map((r) => {
        const c = classify(statusByStyle[s(r.style)]);
        return { ...r, cpscStatus: c.status, actionNeeded: c.action };
      }));
    } catch (e) {
      console.error('CPSC compliance worklist load failed', e); // don't swallow (§8.3)
      setError('Could not load the CPSC worklist. Confirm access and retry.');
    } finally { setLoading(false); }
  }, [isAdmin, tenantId]);

  useEffect(() => { load(); }, [load]);

  const toggleSort = useCallback((key) => setSort((p) => (p.key === key ? { key, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' })), []);

  const display = useMemo(() => {
    let r = rows;
    if (onlyNeeds) r = r.filter((x) => x.actionNeeded);
    const f = filter.trim().toLowerCase();
    if (f) r = r.filter((x) => COLUMNS.some((c) => String(x[c.field] ?? '').toLowerCase().includes(f)));
    if (sort.key) {
      const col = COLUMNS.find((c) => c.field === sort.key);
      const dir = sort.dir === 'asc' ? 1 : -1;
      r = [...r].sort((a, b) => (col && col.type === 'number'
        ? ((Number(a[sort.key]) || 0) - (Number(b[sort.key]) || 0)) * dir
        : String(a[sort.key] ?? '').localeCompare(String(b[sort.key] ?? '')) * dir));
    }
    return r;
  }, [rows, onlyNeeds, filter, sort]);

  const exportXlsx = () => {
    const data = display.map((r) => Object.fromEntries(COLUMNS.map((c) => [c.label, r[c.field]])));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'CPSC Worklist');
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `CPSC_Compliance_Worklist_${stamp}.xlsx`);
  };

  if (!isAdmin) {
    return <div className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-4 py-6 text-center">CPSC Compliance Worklist is restricted to administrators.</div>;
  }

  const needCert = rows.filter((r) => r.actionNeeded && r.actionNeeded.startsWith('CPSC')).length;
  const verify = rows.filter((r) => r.actionNeeded && r.actionNeeded.startsWith('Verify')).length;
  const needHts = rows.filter((r) => r.actionNeeded === 'HTS code').length;

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-orange-600" /> CPSC Compliance Worklist</h2>
          <p className="text-sm text-slate-500 mt-0.5">US-CPLAN production orders and what each needs. Export to request documents from production.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="text" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter…" className="px-2 py-1 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400 w-36" />
          <label className="flex items-center gap-1 text-xs text-slate-600"><input type="checkbox" checked={onlyNeeds} onChange={(e) => setOnlyNeeds(e.target.checked)} /> Needs documents only</label>
          <button onClick={load} disabled={loading} className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-50 text-blue-700 rounded hover:bg-blue-100 disabled:opacity-50"><RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Refresh</button>
          <button onClick={exportXlsx} disabled={!display.length} className="flex items-center gap-1 px-2 py-1 text-xs bg-green-50 text-green-700 rounded hover:bg-green-100 disabled:opacity-50"><Download size={12} /> Export</button>
        </div>
      </div>

      <div className="flex gap-4 text-xs text-slate-600">
        <span>In scope: <b>{rows.length}</b></span>
        <span className="text-amber-700">Missing certificate: <b>{needCert}</b></span>
        <span className="text-yellow-800">Verify (CPC): <b>{verify}</b></span>
        <span className="text-slate-500">HTS needed: <b>{needHts}</b></span>
        <span>Showing: <b>{display.length}</b></span>
      </div>

      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {error}</div>}
      {truncated && <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Showing the earliest-shipping {rows.length} rows only — the in-scope set hit the query limit, so this worklist and any export are <strong>incomplete</strong>. Narrow the scope or raise the limit before relying on it.</div>}
      {loading && <div className="text-sm text-slate-500 flex items-center gap-2 py-6 justify-center"><Loader2 className="h-4 w-4 animate-spin" /> Loading worklist + CPSC status…</div>}

      {!loading && !error && (
        <div className="border border-slate-200 rounded-lg overflow-auto max-h-[32rem]">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                {COLUMNS.map((col) => (
                  <th key={col.field} onClick={() => toggleSort(col.field)} title="Click to sort" className={`px-2 py-1.5 font-medium text-slate-600 whitespace-nowrap cursor-pointer select-none hover:bg-slate-100 ${col.align === 'right' ? 'text-right' : 'text-left'}`}>
                    {col.label}{sort.key === col.field ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {display.map((row, idx) => (
                <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                  {COLUMNS.map((col) => (
                    <td key={col.field} className={`px-2 py-1 text-slate-700 whitespace-nowrap ${col.align === 'right' ? 'text-right' : ''}`}>
                      {col.field === 'cpscStatus'
                        ? <span className={`inline-block rounded px-1.5 py-0.5 ${STATUS_TONE[row.cpscStatus] || 'text-slate-600 bg-white'}`}>{row.cpscStatus}</span>
                        : col.field === 'styleDesc'
                          ? <span className="truncate max-w-[220px] inline-block align-middle" title={row.styleDesc}>{row.styleDesc}</span>
                          : col.type === 'number'
                            ? (Number(row[col.field]) || 0).toLocaleString()
                            : (row[col.field] || '')}
                    </td>
                  ))}
                </tr>
              ))}
              {!display.length && <tr><td colSpan={COLUMNS.length} className="px-2 py-8 text-center text-slate-400">No rows{onlyNeeds ? ' need documents' : ''} in scope.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
